import { createContext, useContext, useState, ReactNode } from 'react';
import { Patient, Appointment } from '../types';

interface OpenPatientDetails {
  patient: Patient;
  appointment?: Appointment;
  isMinimized: boolean;
  /** Accordion panel to open first, e.g. 'readings' (labs) when arriving from a lab notification. */
  initialSection?: string;
}

interface PatientDetailsContextType {
  openPatients: Map<string, OpenPatientDetails>;
  openPatient: (patientId: string, patient: Patient, appointment?: Appointment, initialSection?: string) => void;
  closePatient: (patientId: string) => void;
  minimizePatient: (patientId: string) => void;
  restorePatient: (patientId: string) => void;
  updatePatientData: (patientId: string, patient: Patient) => void;
}

const PatientDetailsContext = createContext<PatientDetailsContextType | undefined>(undefined);

export function PatientDetailsProvider({ children }: { children: ReactNode }) {
  const [openPatients, setOpenPatients] = useState<Map<string, OpenPatientDetails>>(new Map());

  const openPatient = (patientId: string, patient: Patient, appointment?: Appointment, initialSection?: string) => {
    setOpenPatients(prev => {
      const newMap = new Map(prev);
      newMap.set(patientId, {
        patient,
        appointment,
        isMinimized: false,
        initialSection
      });
      return newMap;
    });
  };

  const closePatient = (patientId: string) => {
    setOpenPatients(prev => {
      const newMap = new Map(prev);
      newMap.delete(patientId);
      return newMap;
    });
  };

  const minimizePatient = (patientId: string) => {
    setOpenPatients(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(patientId);
      if (existing) {
        newMap.set(patientId, { ...existing, isMinimized: true });
      }
      return newMap;
    });
  };

  const restorePatient = (patientId: string) => {
    setOpenPatients(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(patientId);
      if (existing) {
        newMap.set(patientId, { ...existing, isMinimized: false });
      }
      return newMap;
    });
  };

  const updatePatientData = (patientId: string, patient: Patient) => {
    setOpenPatients(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(patientId);
      if (existing) {
        newMap.set(patientId, { ...existing, patient });
      }
      return newMap;
    });
  };

  return (
    <PatientDetailsContext.Provider
      value={{
        openPatients,
        openPatient,
        closePatient,
        minimizePatient,
        restorePatient,
        updatePatientData
      }}
    >
      {children}
    </PatientDetailsContext.Provider>
  );
}

export function usePatientDetails() {
  const context = useContext(PatientDetailsContext);
  if (!context) {
    throw new Error('usePatientDetails must be used within PatientDetailsProvider');
  }
  return context;
}
